import type { LearnerObject_SkillsWantedV2Input } from '__generated__/graphql-types';

/**
 * A skill the learner has selected for the `skills_wanted_v2` Learner Object
 * attribute. Either a catalog skill (resolved to a `Skill_Skill` id) or a
 * free-text, user-generated skill. The editor keeps this normalized shape in
 * state; the mappers below translate to/from the GraphQL surfaces.
 */
export type SelectedSkill = { type: 'catalog'; id: string; name: string } | { type: 'userGenerated'; name: string };

/**
 * Minimal shape of a single skill in the `attributesByUserId` read fragment
 * (`LearnerObjectAttributesSkillsWanted`): a catalog `Skill_Skill` or a
 * `LearnerObject_UserGeneratedSkill`. Typed structurally so the util does not
 * depend on the full generated query type.
 */
export type LearnerObjectSkillFragment =
  | { __typename: 'Skill_Skill'; id: string; skillName?: string | null }
  | { __typename: 'LearnerObject_UserGeneratedSkill'; name: string };

const normalize = (name: string) => name.trim().toLowerCase();

/** Whether two selected skills refer to the same skill (catalog by id, user-generated by normalized name). */
const isSameSkill = (a: SelectedSkill, b: SelectedSkill): boolean => {
  if (a.type === 'catalog' && b.type === 'catalog') return a.id === b.id;
  if (a.type === 'userGenerated' && b.type === 'userGenerated') return normalize(a.name) === normalize(b.name);
  return false;
};

/** Build the `LearnerObject_UpdateSkillsWantedV2` mutation input from the selected skills. */
export const toSkillsWantedV2Input = (skills: readonly SelectedSkill[]): LearnerObject_SkillsWantedV2Input => {
  const userInterestSkillIds = skills.flatMap((skill) => (skill.type === 'catalog' ? [skill.id] : []));
  const userGeneratedSkills = skills.flatMap((skill) => (skill.type === 'userGenerated' ? [skill.name] : []));
  return {
    userInterestSkillIds: userInterestSkillIds.length > 0 ? userInterestSkillIds : null,
    userGeneratedSkills: userGeneratedSkills.length > 0 ? userGeneratedSkills : null,
  };
};

/** Resolve the read fragment's skills into the editor's normalized shape, preserving order. */
export const fromLearnerObjectSkills = (skills: readonly LearnerObjectSkillFragment[]): SelectedSkill[] =>
  skills.map((skill) =>
    skill.__typename === 'Skill_Skill'
      ? { type: 'catalog', id: skill.id, name: skill.skillName ?? '' }
      : { type: 'userGenerated', name: skill.name }
  );

/** Add a skill unless an equivalent one is already selected (idempotent). */
export const addSkill = (skills: readonly SelectedSkill[], skill: SelectedSkill): SelectedSkill[] =>
  skills.some((existing) => isSameSkill(existing, skill)) ? [...skills] : [...skills, skill];

/** Remove the given skill (catalog by id, user-generated by name). */
export const removeSkill = (skills: readonly SelectedSkill[], skill: SelectedSkill): SelectedSkill[] =>
  skills.filter((existing) => !isSameSkill(existing, skill));
